import React, { Suspense, useEffect, useRef, useState } from "react";
import { useOutletContext } from 'react-router-dom';
import { TabPanel, Tab } from '@grapecity/wijmo.react.nav';
import { useSelector, useDispatch } from 'react-redux';
import { removeTab, setActiveTab, clearTabs, addTab } from '@store/tabSlice';
import '@css/main-view.css';
import pageComponentMap from './PageComponentMap';
import KeepAliveWrapper from './KeepAliveWrapper';
import CompanySelectModal from "@features/Auth/CompanySelectModal";
import { setMenu } from '@store/menuSlice';
import { setPageAuth } from "@store/pageAuthSlice";
import axiosInstance from '@utils/axiosInstance';
import { getBasePageCode } from '@utils/locationUtils';

// 상수 정의
const SYSTEM_ADMIN_USER_ID = '99999';
const DEFAULT_COMPANY_CODE = 'nexol';

export default function MainView() {
    const tabs = useSelector((state) => state.tabs.tabs);
    const activeTab = useSelector((state) => state.tabs.activeTab);
    const [hiddenTabs, setHiddenTabs] = useState(new Set());
    const [isMobile, setIsMobile] = useState(false);

    const { showModal, setShowModal, companies, setCompanies } = useOutletContext();

    const dispatch = useDispatch();

    const tabsRef = useRef(tabs);

    // localStorage에서 userInfo 가져오기
    const getUserInfo = () => {
        const userInfoString = localStorage.getItem('userInfo');
        return userInfoString ? JSON.parse(userInfoString) : null;
    };

    const userInfo = getUserInfo();

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        // 관리자는 초기 로그인 시에만 회사 선택 모달 표시
        if (userInfo?.userId === SYSTEM_ADMIN_USER_ID && userInfo?.companyCode === DEFAULT_COMPANY_CODE) {
            getCompanyList().then(setCompanies);
            setShowModal(true);
        } else {
            getUserMenu();
        }
    }, []);

    useEffect(() => {
        tabsRef.current = tabs;
    }, [tabs]);

    useEffect(() => {
        if (!activeTab || !userInfo) return;

        // 현재 활성 탭의 pageIdx로 권한 찾기
        const currentTab = tabs.find(t => t.pageCode === activeTab);
        const auth = currentTab?.pageIdx
            ? userInfo.pageAuthList?.find(p => p.pageIdx === currentTab.pageIdx)
            : null;

        const finalAuth = auth || {};

        console.log('🔐 권한 적용:', activeTab, 'pageIdx:', currentTab?.pageIdx, '→', finalAuth);

        dispatch(setPageAuth({
            add: !!finalAuth.add,
            save: !!finalAuth.save,
            del: !!finalAuth.del,
            rtv: !!finalAuth.rtv,
        }));

        window.currentCrudActions = window.currentCrudActions || {};
        if (!window.currentCrudActions[activeTab]) {
            window.currentCrudActions[activeTab] = {
                rtv: null,
                add: null,
                save: null,
                del: null,
            };
        }

    }, [activeTab, tabs, dispatch]);

    const getCompanyList = async () => {
        try {
            const response = await axiosInstance.get('/api/companies/getList');
            return response.data;
        } catch (error) {
            console.error('회사 목록 조회 실패:', error);
        }
    }

    const getMenuByCompany = async () => {
        // Token 기반으로 DB에서 최신 권한 조회 (시스템관리자 포함)
        // F5 새로고침 시 최신 권한 자동 반영
        try {
            const response = await axiosInstance.get('/api/menu/tree/current');

            // 응답 구조: { menuTree: [...], pageAuthList: [...] }
            const { menuTree, pageAuthList } = response.data;

            // localStorage의 userInfo 업데이트 (최신 권한 반영)
            const userInfoString = localStorage.getItem('userInfo');
            if (userInfoString) {
                const userInfo = JSON.parse(userInfoString);
                userInfo.pageAuthList = pageAuthList; // 최신 권한으로 업데이트
                localStorage.setItem('userInfo', JSON.stringify(userInfo));
                console.log('✅ 권한 정보 업데이트 완료:', pageAuthList.length, '개 권한');
            }

            return menuTree;
        } catch (error) {
            console.error('메뉴 조회 실패:', error);
            return [];
        }
    }

    const setNewCompanyCodeToken = async (companyCode) => {

        try {
            const response = await axiosInstance.post('/api/companies/admin/switch',{
                companyCode : companyCode
            });
            if (response.data) {
                localStorage.setItem("userInfo", JSON.stringify(response.data));
            }
        } catch (error) {
            console.error('회사 코드 변경 실패:', error);
        }

    }

    const getUserMenu = async () => {
        const menuData = await getMenuByCompany();
        dispatch(clearTabs())
        dispatch(setMenu(menuData));

        // 홈 탭 자동 추가
        dispatch(addTab({ pageCode: 'HomePage', title: '홈' }));
    };

    const handleCompanySelect = async (companyCode) => {
        setShowModal(false);
        await setNewCompanyCodeToken(companyCode);
        const menuData = await getMenuByCompany();
        dispatch(clearTabs())
        dispatch(setMenu(menuData));
        
        // 홈 탭 자동 추가
        dispatch(addTab({ pageCode: 'HomePage', title: '홈' }));
        
        // 회사 변경 이벤트 발생
        window.dispatchEvent(new CustomEvent('companyChanged'));
    };

    const handleTabChange = (e) => {
        const selectedIndex = e.selectedIndex;
        const selectedTab = tabsRef.current[selectedIndex];
        if (selectedTab) {
            dispatch(setActiveTab(selectedTab.pageCode));
        }
    };

    const handleCloseTab = (e, pageCode) => {
        e.stopPropagation();
        dispatch(removeTab(pageCode));
    };

    const handleDrop = (pageCode) => {
        setHiddenTabs(prev => new Set(prev).add(pageCode));
    };

    const activeTabIndex = tabs.findIndex(tab => tab.pageCode === activeTab);

    useEffect(() => {
        setHiddenTabs(prev => {
            const newSet = new Set(prev);
            newSet.delete(activeTab);
            return newSet;
        });
    }, [activeTab]);

    // 탭이 있는 환경에서는 적절한 마진 적용
    const getTopMargin = () => {
        if (window.innerWidth <= 480) return 60;
        if (window.innerWidth <= 768) return 70;
        return 80;
    };

    return (
        <>
            <CompanySelectModal
                show={showModal}
                btnClick={handleCompanySelect}
                companies={companies}
            />
            <div style={{height: isMobile ? '100%' : '95%'}}>
                <TabPanel
                    id="main-tab-panel"
                    selectedIndex={activeTabIndex}
                    selectedIndexChanged={handleTabChange}
                >
                    {tabs.map((tab) => (
                        <Tab key={tab.pageCode}>
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <span>{tab.title}</span>
                                <button
                                    className="main-tab-close-btn"
                                    onClick={(e) => handleCloseTab(e, tab.pageCode)}
                                >
                                    ✕
                                </button>
                            </div>
                            <div></div>
                        </Tab>
                    ))}
                </TabPanel>

                <div style={{ 
                    padding: '0px 10px 0px 10px', 
                    height: isMobile ? '100%' : '99%'
                }} className="dashboard-container">
                    {tabs.length === 0 && (
                        <div style={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            color: '#666',
                            fontSize: '16px'
                        }}>
                            탭을 선택해주세요.
                        </div>
                    )}
                    {tabs.map(tab => {
                        const isActive = tab.pageCode === activeTab;
                        // tab.pageCode에서 기본 pageCode 추출 (location 제거)
                        const originalPageCode = getBasePageCode(tab.pageCode);
                        const PageComponent = pageComponentMap[originalPageCode];
                        // hiddenTabs에 포함된 탭은 렌더링하지 않음
                        if (hiddenTabs.has(tab.pageCode)) return null;

                        return (
                            <div
                                key={tab.pageCode}
                                style={{ display: isActive ? 'block' : 'none', height: '100%' }}
                            >
                                {PageComponent ? (
                                    <KeepAliveWrapper
                                        tab={tab}
                                        activeTab={activeTab}
                                        onDrop={handleDrop}
                                    >
                                        <Suspense fallback={<div>로딩 중...</div>}>
                                            <PageComponent
                                                key={tab.pageCode}
                                                tabPageCode={tab.pageCode}
                                                isActiveTab={isActive}
                                            />
                                        </Suspense>
                                    </KeepAliveWrapper>
                                ) : (
                                    <div>페이지를 찾을 수 없습니다.</div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </>
    );
}
